import type { ReactNode } from "react";
import { motion, LayoutGroup } from "motion/react";
import type { LucideIcon } from "lucide-react";
import { SPRING_INDICATOR } from "@/shared/motion/transitions";

export interface SettingsTab {
  id: string;
  label: string;
  icon: LucideIcon;
}

/** Goldsmith-style settings layout: sticky left tab rail (animated pill) + content. */
export default function SettingsShell({
  tabs,
  active,
  onChange,
  children,
}: {
  tabs: SettingsTab[];
  active: string;
  onChange: (id: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-[1100px] px-4 py-6 sm:px-6 sm:py-8">
      <h1 className="font-headline text-[26px] leading-tight text-ink-900">Settings</h1>
      <p className="mt-0.5 text-[13px] text-ink-500">Manage your profile, workspace, and security.</p>

      {/* Mobile tab select */}
      <div className="mt-5 md:hidden">
        <select
          value={active}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-lg border border-ink-200 px-3 py-2 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
        >
          {tabs.map((t) => (
            <option key={t.id} value={t.id}>{t.label}</option>
          ))}
        </select>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[200px_1fr]">
        {/* Desktop tab rail */}
        <nav className="sticky top-6 hidden h-fit flex-col gap-0.5 md:flex">
          <LayoutGroup id="settings-tabs">
            {tabs.map((t) => {
              const isActive = t.id === active;
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => onChange(t.id)}
                  className={`relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-left text-[13px] font-medium transition-colors ${
                    isActive ? "text-white" : "text-ink-500 hover:bg-ink-50 hover:text-ink-900"
                  }`}
                >
                  {isActive && (
                    <motion.span
                      layoutId="settings-tab-pill"
                      transition={SPRING_INDICATOR}
                      className="absolute inset-0 rounded-lg bg-ink-900"
                    />
                  )}
                  <Icon size={16} className="relative z-10" />
                  <span className="relative z-10">{t.label}</span>
                </button>
              );
            })}
          </LayoutGroup>
        </nav>

        {/* Content */}
        <motion.div key={active} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="min-w-0">
          {children}
        </motion.div>
      </div>
    </div>
  );
}
